import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from '../common/primitives.js';
import { distributionSummarySchema, founderDistributionsSchema, } from '../distributions/distributions.js';
import { cashCurrencyTotalSchema, isoMonthSchema, pnlFiguresSchema } from '../finance/finance.js';
import { founderRefSchema } from '../funding/funding.js';
import { memberRefSchema } from '../governance/topics.js';
import { outstandingActionSchema } from '../cabinet/cabinet.js';
import { agingBlockerSchema } from './reports.js';
import { taskSummarySchema } from '../tasks/tasks.js';
import { reportingPeriodStatusSchema, reportStateSchema } from '../updates/updates.js';
import { valuationSnapshotSummarySchema } from '../valuation/valuation.js';
/**
 * How many distribution proposals the workspace report lists. Older ones stay on the
 * distributions page; the flag says the list was cut.
 */
export const DISTRIBUTION_HISTORY_LIMIT = 8;
/** Open obligation periods due inside this many milliseconds count as due soon. */
export const OBLIGATION_DUE_SOON_MS = 7 * 24 * 60 * 60 * 1000;
export const NO_REPORTING_CURRENCY_NOTE = 'No reporting currency is set, so amounts stay in their own currencies.';
export const MISSING_RATE_NOTE = 'A combined total is omitted because an exchange rate is missing.';
const combinedPnlSchema = pnlFiguresSchema.extend({ currency: z.string() });
export const monthFactsSchema = z.object({
    month: isoMonthSchema,
    byCurrency: z.array(pnlFiguresSchema.extend({ currency: z.string() })),
    /** Set only when every currency in the month converted. */
    combined: combinedPnlSchema.nullable(),
    note: z.string().nullable(),
});
export const cashFactsSchema = z.object({
    totals: z.array(cashCurrencyTotalSchema),
    /** Set only when every currency converted. */
    combined: z.object({ currency: z.string(), book: z.string(), operating: z.string() }).nullable(),
    note: z.string().nullable(),
});
export const reserveLineSchema = z.object({
    currency: z.string(),
    calculatedOn: isoDateSchema,
    operatingCash: z.string(),
    /** Null when no policy is in force or the reserve cannot be computed. */
    reserveRequired: z.string().nullable(),
    /** Null when the reserve cannot be computed. Not an approval to pay anyone. */
    distributable: z.string().nullable(),
    policySummary: z.string().nullable(),
});
export const fundingDueFactsSchema = z.object({
    /** Calls whose due date falls in this window (drafts and cancelled calls excluded). */
    period: z.object({ from: isoDateSchema, to: isoDateSchema }),
    openCalls: z.number().int(),
    byCurrency: z.array(z.object({
        currency: z.string(),
        outstanding: z.string(),
        overdue: z.string(),
        calls: z.number().int(),
    })),
});
export const valuationFactsSchema = z.object({
    latest: valuationSnapshotSummarySchema.nullable(),
    disclaimer: z.string(),
});
/** Company figures a member with finance access may see. Amounts stay per currency unless combined. */
export const financeFactsSchema = z.object({
    asOf: isoDateSchema,
    month: monthFactsSchema,
    cash: cashFactsSchema,
    reserve: z.array(reserveLineSchema),
    fundingDue: fundingDueFactsSchema,
    valuation: valuationFactsSchema,
});
export const personalPositionSchema = financeFactsSchema.extend({
    /** Null when the member has no founder profile. */
    myDistributions: founderDistributionsSchema.nullable(),
});
/** Owes and is-owed stay separate. There is no net column to sort people by. */
export const founderBalanceLineSchema = z.object({
    founder: founderRefSchema,
    currency: z.string(),
    owes: z.string(),
    isOwed: z.string(),
});
export const companyDashboardSchema = financeFactsSchema.extend({
    founderBalances: z.array(founderBalanceLineSchema),
    distributions: z.array(distributionSummarySchema),
    distributionsTruncated: z.boolean(),
});
export const weeklyPulseCountsSchema = z.object({
    submitted: z.number().int(),
    late: z.number().int(),
    overdue: z.number().int(),
    missing: z.number().int(),
    excused: z.number().int(),
    draft: z.number().int(),
    notStarted: z.number().int(),
});
/** Latest reporting period. Counts are per state; nothing here is a completion percentage. */
export const weeklyPulseSchema = z.object({
    periodId: uuidSchema,
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    dueAt: isoDateTimeSchema,
    status: reportingPeriodStatusSchema,
    counts: weeklyPulseCountsSchema,
    rows: z.array(z.object({ member: memberRefSchema, state: reportStateSchema })),
});
export const OBLIGATION_PULSE_SCOPES = ['WORKSPACE', 'MINE'];
export const obligationPulseScopeSchema = z.enum(OBLIGATION_PULSE_SCOPES);
/** Counts of records. `missed` is how many periods were recorded missed, not a rate. */
export const obligationPulseSchema = z.object({
    scope: obligationPulseScopeSchema,
    active: z.number().int(),
    dueSoon: z.number().int(),
    openAtRisk: z.number().int(),
    missed: z.number().int(),
});
/**
 * Workspace report (spec §7.2 pulse and §7.8). Sections the caller cannot see are null.
 * People are alphabetical. No score, rank, or net column.
 */
export const masterDashboardSchema = z.object({
    asOf: isoDateTimeSchema,
    decisionsAwaitingMe: z.array(outstandingActionSchema),
    myOverdueTasks: z.array(outstandingActionSchema),
    blockersWaitingOnMe: z.array(outstandingActionSchema),
    overdueTasks: z.array(taskSummarySchema),
    overdueTaskCount: z.number().int(),
    openBlockerCount: z.number().int(),
    oldestBlockers: z.array(agingBlockerSchema),
    weeklyUpdate: weeklyPulseSchema.nullable(),
    obligations: obligationPulseSchema.nullable(),
    /** Null without finance access. */
    company: companyDashboardSchema.nullable(),
});
//# sourceMappingURL=dashboards.js.map